import React, { useEffect, useRef, useState, useCallback } from 'react';
import './RecruitmentMetrics.scss';

const RecruitmentMetrics = ({ 
  initialData = {
    timeToFill: 23,
    timeToHire: 18,
    timeInStage: 5,
    qualityOfHire: 8.2,
    offerAcceptanceRate: 89,
    firstYearRetention: 94,
    costPerHire: 4250,
    applicationToInterviewRate: 12,
    interviewToOfferRate: 35,
    hiresPerRecruiter: 3.2,
    requisitionFillRate: 78,
    candidateNPS: 42,
    hiringVelocity: 1.8,
    pipelineEfficiency: 0.28,
    recruitmentROI: 245,
    diversityHiringRate: 67,
    pipelineDiversity: 54,
    totalHires: 156,
    avgDaysToFill: 23,
    currentCostPerHire: 4250,
    currentOfferRate: 89,
    currentRetentionRate: 94,
    currentNPS: 42
  }
}) => {
  const cardsRef = useRef([]);
  const [selectedPeriod, setSelectedPeriod] = useState('month');
  const [metricsData, setMetricsData] = useState(initialData);
  const [isLoading, setIsLoading] = useState(false);

  // Sample data for different time periods
  const periodData = {
    day: {
      ...initialData,
      totalHires: 2,
      avgDaysToFill: 25,
      currentCostPerHire: 4500,
      currentOfferRate: 85,
      currentRetentionRate: 92,
      currentNPS: 38,
      timeToFill: 25,
      timeToHire: 20,
      timeInStage: 3,
      qualityOfHire: 7.8,
      offerAcceptanceRate: 85,
      firstYearRetention: 92,
      costPerHire: 4500,
      applicationToInterviewRate: 10,
      interviewToOfferRate: 32,
      hiresPerRecruiter: 0.1,
      requisitionFillRate: 75,
      candidateNPS: 38,
      hiringVelocity: 0.1,
      pipelineEfficiency: 0.15,
      recruitmentROI: 220,
      diversityHiringRate: 60,
      pipelineDiversity: 50
    },
    week: {
      ...initialData,
      totalHires: 12,
      avgDaysToFill: 24,
      currentCostPerHire: 4350,
      currentOfferRate: 87,
      currentRetentionRate: 93,
      currentNPS: 40,
      timeToFill: 24,
      timeToHire: 19,
      timeInStage: 4,
      qualityOfHire: 8.0,
      offerAcceptanceRate: 87,
      firstYearRetention: 93,
      costPerHire: 4350,
      applicationToInterviewRate: 11,
      interviewToOfferRate: 33,
      hiresPerRecruiter: 0.8,
      requisitionFillRate: 76,
      candidateNPS: 40,
      hiringVelocity: 0.6,
      pipelineEfficiency: 0.21,
      recruitmentROI: 232,
      diversityHiringRate: 64,
      pipelineDiversity: 52
    },
    month: initialData,
    quarter: {
      ...initialData,
      totalHires: 456,
      avgDaysToFill: 22,
      currentCostPerHire: 4100,
      currentOfferRate: 91,
      currentRetentionRate: 95,
      currentNPS: 45,
      timeToFill: 22,
      timeToHire: 17,
      timeInStage: 6,
      qualityOfHire: 8.5,
      offerAcceptanceRate: 91,
      firstYearRetention: 95,
      costPerHire: 4100,
      applicationToInterviewRate: 14,
      interviewToOfferRate: 38,
      hiresPerRecruiter: 9.5,
      requisitionFillRate: 82,
      candidateNPS: 45,
      hiringVelocity: 5.2,
      pipelineEfficiency: 0.35,
      recruitmentROI: 268,
      diversityHiringRate: 70,
      pipelineDiversity: 58
    }
  };

  // Dynamic data filtering function
  const filterDataByPeriod = useCallback((period) => {
    setIsLoading(true);
    
    // Simulate API call delay
    setTimeout(() => {
      const newData = periodData[period] || initialData;
      setMetricsData(newData);
      setIsLoading(false);
    }, 300);
  }, [initialData]);

  // Handle filter change
  const handleFilterChange = (e) => {
    const newPeriod = e.target.value;
    setSelectedPeriod(newPeriod);
    filterDataByPeriod(newPeriod);
  };

  useEffect(() => {
    // Intersection Observer for animation
    const observerOptions = {
      threshold: 0.1,
      rootMargin: '0px 0px -50px 0px'
    };

    const observer = new IntersectionObserver((entries) => {
      entries.forEach(entry => {
        if (entry.isIntersecting) {
          entry.target.style.opacity = '1';
          entry.target.style.transform = 'translateY(0)';
        }
      });
    }, observerOptions);

    // Initially hide cards and animate them in
    cardsRef.current.forEach((card, index) => {
      if (card) {
        card.style.opacity = '0';
        card.style.transform = 'translateY(20px)';
        card.style.transition = `opacity 0.6s ease ${index * 0.1}s, transform 0.6s ease ${index * 0.1}s`;
        observer.observe(card);
      }
    });

    return () => observer.disconnect();
  }, []);

  const handleMetricClick = (e, metricName) => {
    const item = e.currentTarget;
    item.style.transform = 'scale(0.98)';
    setTimeout(() => {
      item.style.transform = '';
    }, 150);
    
    console.log(`Clicked metric: ${metricName}`);
  };

  const addToRefs = (el) => {
    if (el && !cardsRef.current.includes(el)) {
      cardsRef.current.push(el);
    }
  };

  const formatCurrency = (value) => {
    return new Intl.NumberFormat('en-US', {
      style: 'currency',
      currency: 'USD',
      minimumFractionDigits: 0,
      maximumFractionDigits: 0,
    }).format(value);
  };

  return (
    <div className="analytics-dashboard">
      <div className="dashboard-container">
        {/* Header */}
        <div className="dashboard-header">
          <div className="header-content">
            <h1>Recruitment Metrics Dashboard</h1>
            <p>Track, analyze & optimize your recruitment performance</p>
          </div>
          <div className="time-filter">
            <select 
              className={`filter-dropdown ${isLoading ? 'loading' : ''}`}
              value={selectedPeriod}
              onChange={handleFilterChange}
              disabled={isLoading}
            >
              <option value="day">Day</option>
              <option value="week">Week</option>
              <option value="month">Month</option>
              <option value="quarter">Quarter</option>
            </select>
            {isLoading && (
              <div className="filter-loading">
                <div className="loading-spinner"></div>
              </div>
            )}
          </div>
        </div>

        {/* Key Performance Insights */}
        <div className="key-insights-section">
          <div className="insights-grid">
            <div className="insight-card" ref={addToRefs}>
              <div className="insight-header">
                <div className="insight-info">
                  <span className="insight-label">Total Hires</span>
                  <span className="insight-sublabel">This Quarter</span>
                </div>
                <div className="insight-icon blue">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M16 4c0-1.11.89-2 2-2s2 .89 2 2-.89 2-2 2-2-.89-2-2zm4 18v-6h2.5l-2.54-7.63A2.99 2.99 0 0 0 17.1 7H16c-.8 0-1.56.31-2.11.86L12 9.75l-1.89-1.89C9.56 7.31 8.8 7 8 7H6.9a2.99 2.99 0 0 0-2.86 2.13L1.5 16H4v6h16z"/>
                  </svg>
                </div>
              </div>
              <div className="insight-value">{metricsData.totalHires}</div>
              <div className="insight-trend positive">+12%</div>
            </div>

            <div className="insight-card" ref={addToRefs}>
              <div className="insight-header">
                <div className="insight-info">
                  <span className="insight-label">Avg. Days to Fill</span>
                  <span className="insight-sublabel">Time to hire</span>
                </div>
                <div className="insight-icon orange">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1 15h-2v-6h2v6zm0-8h-2V7h2v2z"/>
                  </svg>
                </div>
              </div>
              <div className="insight-value">{metricsData.avgDaysToFill}</div>
              <div className="insight-trend negative">-5%</div>
            </div>

            <div className="insight-card" ref={addToRefs}>
              <div className="insight-header">
                <div className="insight-info">
                  <span className="insight-label">Cost Per Hire</span>
                  <span className="insight-sublabel">Average cost</span>
                </div>
                <div className="insight-icon green">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2C6.48 2 2 6.48 2 12s4.48 10 10 10 10-4.48 10-10S17.52 2 12 2zm1.41 6.41L16.5 11.5h-4v4h-2v-4h-4l3.09-3.09L12 6l1.41 2.41z"/>
                  </svg>
                </div>
              </div>
              <div className="insight-value">{formatCurrency(metricsData.currentCostPerHire)}</div>
              <div className="insight-trend positive">-15%</div>
            </div>

            <div className="insight-card" ref={addToRefs}>
              <div className="insight-header">
                <div className="insight-info">
                  <span className="insight-label">Offer Acceptance</span>
                  <span className="insight-sublabel">Success rate</span>
                </div>
                <div className="insight-icon purple">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M9 16.2L4.8 12l-1.4 1.4L9 19 21 7l-1.4-1.4L9 16.2z"/>
                  </svg>
                </div>
              </div>
              <div className="insight-value">{metricsData.currentOfferRate}%</div>
              <div className="insight-trend positive">+3%</div>
            </div>

            <div className="insight-card" ref={addToRefs}>
              <div className="insight-header">
                <div className="insight-info">
                  <span className="insight-label">First Year Retention</span>
                  <span className="insight-sublabel">Employee retention</span>
                </div>
                <div className="insight-icon teal">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M7.41 15.41L12 10.83l4.59 4.58L18 14l-6-6-6 6 1.41 1.41z"/>
                  </svg>
                </div>
              </div>
              <div className="insight-value">{metricsData.currentRetentionRate}%</div>
              <div className="insight-trend positive">+8%</div>
            </div>

            <div className="insight-card" ref={addToRefs}>
              <div className="insight-header">
                <div className="insight-info">
                  <span className="insight-label">Candidate NPS</span>
                  <span className="insight-sublabel">Experience score</span>
                </div>
                <div className="insight-icon indigo">
                  <svg width="20" height="20" viewBox="0 0 24 24" fill="currentColor">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                </div>
              </div>
              <div className="insight-value">+{metricsData.currentNPS}</div>
              <div className="insight-trend positive">+8pts</div>
            </div>
          </div>
        </div>

        {/* Detailed Metrics Grid */}
        <div className="metrics-grid">
          {/* Time-Based Metrics */}
          <div className="metric-card" ref={addToRefs}>
            <div className="metric-header">
              <div className="metric-title">Time-Based Metrics</div>
              <div className="metric-icon orange">⏱️</div>
            </div>
            <div className="metric-content">
              <div className="metric-item" onClick={(e) => handleMetricClick(e, 'Time to Fill')}>
                <div className="metric-name">Time to Fill</div>
                <div className="metric-formula">Date Position Filled - Date Position Posted</div>
                <div className="metric-current">
                  <span className="metric-label">Current Average:</span>
                  <span className="metric-number">{metricsData.timeToFill} days</span>
                </div>
              </div>
              <div className="metric-item" onClick={(e) => handleMetricClick(e, 'Time to Hire')}>
                <div className="metric-name">Time to Hire</div>
                <div className="metric-formula">Date Offer Accepted - Date Candidate Applied</div>
                <div className="metric-current">
                  <span className="metric-label">Current Average:</span>
                  <span className="metric-number">{metricsData.timeToHire} days</span>
                </div>
              </div>
              <div className="metric-item" onClick={(e) => handleMetricClick(e, 'Time in Stage')}>
                <div className="metric-name">Time in Stage</div>
                <div className="metric-formula">Exit Date from Stage - Entry Date to Stage</div>
                <div className="metric-current">
                  <span className="metric-label">Interview Stage:</span>
                  <span className="metric-number">{metricsData.timeInStage} days</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Quality Metrics */}
          <div className="metric-card" ref={addToRefs}>
            <div className="metric-header">
              <div className="metric-title">Quality Metrics</div>
              <div className="metric-icon purple">⭐</div>
            </div>
            <div className="metric-content">
              <div className="metric-item" onClick={(e) => handleMetricClick(e, 'Quality of Hire')}>
                <div className="metric-name">Quality of Hire</div>
                <div className="metric-formula">(Performance Rating + Retention + Cultural Fit) ÷ 3</div>
                <div className="metric-current">
                  <span className="metric-label">Current Score:</span>
                  <span className="metric-number">{metricsData.qualityOfHire}/10</span>
                </div>
              </div>
              <div className="metric-item" onClick={(e) => handleMetricClick(e, 'Offer Acceptance Rate')}>
                <div className="metric-name">Offer Acceptance Rate</div>
                <div className="metric-formula">(Offers Accepted ÷ Offers Extended) × 100</div>
                <div className="metric-current">
                  <span className="metric-label">Current Rate:</span>
                  <span className="metric-number">{metricsData.offerAcceptanceRate}%</span>
                </div>
              </div>
              <div className="metric-item" onClick={(e) => handleMetricClick(e, 'First Year Retention')}>
                <div className="metric-name">First Year Retention</div>
                <div className="metric-formula">(Employees After 1 Year ÷ Total Hires) × 100</div>
                <div className="metric-current">
                  <span className="metric-label">Current Rate:</span>
                  <span className="metric-number">{metricsData.firstYearRetention}%</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Efficiency Metrics */}
          <div className="metric-card" ref={addToRefs}>
            <div className="metric-header">
              <div className="metric-title">Efficiency Metrics</div>
              <div className="metric-icon green">💰</div>
            </div>
            <div className="metric-content">
              <div className="metric-item" onClick={(e) => handleMetricClick(e, 'Cost Per Hire')}>
                <div className="metric-name">Cost Per Hire</div>
                <div className="metric-formula">(Internal Costs + External Costs) ÷ Number of Hires</div>
                <div className="metric-current">
                  <span className="metric-label">Current Cost:</span>
                  <span className="metric-number">{formatCurrency(metricsData.costPerHire)}</span>
                </div>
              </div>
              <div className="metric-item" onClick={(e) => handleMetricClick(e, 'Application to Interview Rate')}>
                <div className="metric-name">Application to Interview Rate</div>
                <div className="metric-formula">(Interviews Scheduled ÷ Applications Received) × 100</div>
                <div className="metric-current">
                  <span className="metric-label">Current Rate:</span>
                  <span className="metric-number">{metricsData.applicationToInterviewRate}%</span>
                </div>
              </div>
              <div className="metric-item" onClick={(e) => handleMetricClick(e, 'Interview to Offer Rate')}>
                <div className="metric-name">Interview to Offer Rate</div>
                <div className="metric-formula">(Offers Extended ÷ Interviews Conducted) × 100</div>
                <div className="metric-current">
                  <span className="metric-label">Current Rate:</span>
                  <span className="metric-number">{metricsData.interviewToOfferRate}%</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Volume & Capacity Metrics */}
          <div className="metric-card" ref={addToRefs}>
            <div className="metric-header">
              <div className="metric-title">Volume & Capacity</div>
              <div className="metric-icon blue">📈</div>
            </div>
            <div className="metric-content">
              <div className="metric-item" onClick={(e) => handleMetricClick(e, 'Hires per Recruiter')}>
                <div className="metric-name">Hires per Recruiter</div>
                <div className="metric-formula">Total Hires ÷ Number of Recruiters</div>
                <div className="metric-current">
                  <span className="metric-label">Current Average:</span>
                  <span className="metric-number">{metricsData.hiresPerRecruiter}/month</span>
                </div>
              </div>
              <div className="metric-item" onClick={(e) => handleMetricClick(e, 'Requisition Fill Rate')}>
                <div className="metric-name">Requisition Fill Rate</div>
                <div className="metric-formula">(Positions Filled ÷ Total Open Positions) × 100</div>
                <div className="metric-current">
                  <span className="metric-label">Current Rate:</span>
                  <span className="metric-number">{metricsData.requisitionFillRate}%</span>
                </div>
              </div>
              <div className="metric-item" onClick={(e) => handleMetricClick(e, 'Candidate Experience Score')}>
                <div className="metric-name">Candidate Experience Score</div>
                <div className="metric-formula">% Promoters - % Detractors (NPS)</div>
                <div className="metric-current">
                  <span className="metric-label">Current NPS:</span>
                  <span className="metric-number">+{metricsData.candidateNPS}</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Advanced Analytics */}
          <div className="metric-card" ref={addToRefs}>
            <div className="metric-header">
              <div className="metric-title">Advanced Analytics</div>
              <div className="metric-icon pink">🚀</div>
            </div>
            <div className="metric-content">
              <div className="metric-item" onClick={(e) => handleMetricClick(e, 'Hiring Velocity')}>
                <div className="metric-name">Hiring Velocity</div>
                <div className="metric-formula">Number of Hires ÷ Average Time to Hire</div>
                <div className="metric-current">
                  <span className="metric-label">Current Velocity:</span>
                  <span className="metric-number">{metricsData.hiringVelocity}/day</span>
                </div>
              </div>
              <div className="metric-item" onClick={(e) => handleMetricClick(e, 'Pipeline Efficiency')}>
                <div className="metric-name">Pipeline Efficiency</div>
                <div className="metric-formula">(Hires ÷ Sourced Candidates) × (1 ÷ Avg Time to Hire)</div>
                <div className="metric-current">
                  <span className="metric-label">Efficiency Score:</span>
                  <span className="metric-number">{metricsData.pipelineEfficiency}</span>
                </div>
              </div>
              <div className="metric-item" onClick={(e) => handleMetricClick(e, 'Recruitment ROI')}>
                <div className="metric-name">Recruitment ROI</div>
                <div className="metric-formula">((Value of Hire - Cost Per Hire) ÷ Cost Per Hire) × 100</div>
                <div className="metric-current">
                  <span className="metric-label">Current ROI:</span>
                  <span className="metric-number">{metricsData.recruitmentROI}%</span>
                </div>
              </div>
            </div>
          </div>
          
          {/* Diversity Metrics */}
          <div className="metric-card" ref={addToRefs}>
            <div className="metric-header">
              <div className="metric-title">Diversity Metrics</div>
              <div className="metric-icon yellow">🌈</div>
            </div>
            <div className="metric-content">
              <div className="metric-item" onClick={(e) => handleMetricClick(e, 'Diversity Hiring Rate')}>
                <div className="metric-name">Diversity Hiring Rate</div>
                <div className="metric-formula">(Diverse Hires ÷ Total Hires) × 100</div>
                <div className="metric-current">
                  <span className="metric-label">Current Rate:</span>
                  <span className="metric-number">{metricsData.diversityHiringRate}%</span>
                </div>
              </div>
              <div className="metric-item" onClick={(e) => handleMetricClick(e, 'Pipeline Diversity')}>
                <div className="metric-name">Pipeline Diversity</div>
                <div className="metric-formula">(Diverse Candidates at Stage ÷ Total Candidates) × 100</div>
                <div className="metric-current">
                  <span className="metric-label">Application Stage:</span>
                  <span className="metric-number">{metricsData.pipelineDiversity}%</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default RecruitmentMetrics;